import { UserRole } from "@/enums/auth";
import { Subject } from "@/types/subject-schema";
import { Session } from "next-auth";
import { notFound } from "next/navigation";
import { getMainMetaMap } from "./dataFetching";
import { titleToUrl } from "./routing";

export function userHasAccessToSubject(
  session: Session,
  subjectDocumentId: Subject["documentId"]
) {
  return session.user.subscriptionMeta?.activeEntitlementSubjects?.includes(
    subjectDocumentId
  );
}

export function userHasAdminRights(userRole: UserRole | undefined) {
  return userRole === "Super Admin" || userRole === "Admin";
}

export function userIsSuperAdmin(userRole: UserRole | undefined) {
  return userRole === "Super Admin";
}

export async function getSubjectBaseMetaFromParams(params: {
  subject: string;
}) {
  const MainMetaMap = await getMainMetaMap();

  let subjectDocumentId: string | undefined = undefined;
  let subjectName: string | undefined = undefined;
  const topicDocumentIds: string[] = [];
  const topicTitles: { sl: string[]; hl: string[] } = {
    sl: [],
    hl: [],
  };
  // find the subject document id
  const subjectMeta = Object.entries(MainMetaMap).find(
    ([id, meta]) => titleToUrl(meta.title) === params.subject
  )!;
  subjectDocumentId = subjectMeta[0];
  subjectName = subjectMeta[1].title;
  // find the topic document ids
  Object.entries(MainMetaMap).forEach(([id, meta]) => {
    if (
      "subjectDocumentId" in meta &&
      meta.subjectDocumentId === subjectDocumentId
    ) {
      topicDocumentIds.push(id);
      if (meta.isHL) {
        topicTitles.hl.push(meta.title);
      } else {
        topicTitles.sl.push(meta.title);
      }
    }
  });

  // if issues with finding the subject, return not found
  if (
    !subjectDocumentId ||
    !subjectName ||
    topicDocumentIds.length === 0 ||
    (topicTitles.sl.length === 0 && topicTitles.hl.length === 0)
  ) {
    return notFound();
  }
  // return the params
  return {
    subjectDocumentId,
    subjectName,
    topicDocumentIds,
    topicTitles,
  };
}

export async function getTopicBaseMetaFromParams(params: {
  subject: string;
  topic: string;
  subTopic?: string;
}) {
  const MainMetaMap = await getMainMetaMap();
  const subjectDocumentId = Object.entries(MainMetaMap).find(
    ([id, meta]) => titleToUrl(meta.title) === params.subject
  )![0];
  let topicDocumentId: string | undefined = undefined;
  let subTopicDocumentIds: string[] | undefined = undefined;
  let currentSubTopicDocumentId: string | undefined = undefined;

  // Single pass: find current topic
  for (const [id, meta] of Object.entries(MainMetaMap)) {
    if (
      "subjectDocumentId" in meta &&
      meta.subjectDocumentId === subjectDocumentId
    ) {
      // Check if this is the current topic
      if (params.topic.includes(titleToUrl(meta.title))) {
        topicDocumentId = id;
      }
    }
  }

  // Single pass: find subtopics (only if we found a topic)
  if (topicDocumentId) {
    subTopicDocumentIds = [];

    for (const [id, meta] of Object.entries(MainMetaMap)) {
      if (
        "topicDocumentId" in meta &&
        meta.topicDocumentId === topicDocumentId
      ) {
        subTopicDocumentIds.push(id);
        if (params.subTopic && params.subTopic === titleToUrl(meta.title)) {
          currentSubTopicDocumentId = id;
        }
      }
    }
  }
  // if issues with finding the topic, return not found
  if (!subjectDocumentId || !topicDocumentId || !subTopicDocumentIds) {
    return notFound();
  }
  // return the params
  return {
    subjectDocumentId,
    topicDocumentId,
    subTopicDocumentIds,
    currentSubTopicDocumentId,
  };
}
